import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--nw-radius-control)] text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-[var(--nw-action-hover)] disabled:opacity-100 disabled:bg-[var(--nw-accent-disabled)] disabled:text-[var(--nw-accent-disabled-ink)]",
        destructive:
          "border border-[var(--nw-border-strong)] bg-card text-destructive hover:border-[var(--nw-danger-border)] hover:bg-[var(--nw-danger-soft)] focus-visible:ring-destructive/20",
        outline:
          "border border-[var(--nw-border-strong)] bg-card text-foreground hover:border-[var(--nw-border-input)] hover:bg-[var(--nw-surface-muted)]",
        secondary:
          "border border-[var(--nw-border-strong)] bg-card text-foreground hover:border-[var(--nw-border-input)] hover:bg-[var(--nw-surface-muted)]",
        ghost:
          "text-foreground hover:bg-[var(--nw-surface-muted)]",
        link: "text-[var(--nw-accent)] underline decoration-[var(--nw-accent-muted)] underline-offset-4 hover:decoration-current",
      },
      size: {
        default: "h-11 md:h-[38px] px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-[var(--nw-radius-sm)] px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 md:h-8 gap-1.5 px-3 text-[13px] has-[>svg]:px-2.5",
        lg: "h-12 px-[22px] text-base has-[>svg]:px-4",
        icon: "size-11 md:size-9",
        "icon-xs": "size-6 rounded-[var(--nw-radius-sm)] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
